// Pricing engine — pure functions that turn a BOM + pricing config into
// a customer-visible breakdown.
//
// Cost-plus markup model:
//
//   subtotal      = materials + edges + hardware + labor
//   markupAmount  = subtotal × markup
//   preTax        = subtotal + markupAmount
//   taxAmount     = preTax × taxRate
//   total         = preTax + taxAmount + delivery
//
// Defaults are conservative for a small US kitchen shop. Designers can
// override per-project via <PricingEditor>.

import type {
  PricingConfig,
  Project,
  QuoteBreakdown,
  USD,
} from "./types";
import type { ProjectBom } from "./cabinets/bom";

/** Sensible defaults for a new US-market project. */
export const DEFAULT_PRICING: PricingConfig = {
  laborPerCabinet: 150,
  markup:          0.50,   // 50%
  taxRate:         0.08,   // 8%
  delivery:        150,
};

/** Resolve a project's pricing config, applying defaults for missing fields. */
export function pricingFor(p: Project | null | undefined): PricingConfig {
  if (!p?.pricing) return DEFAULT_PRICING;
  return {
    laborPerCabinet: p.pricing.laborPerCabinet ?? DEFAULT_PRICING.laborPerCabinet,
    markup:          p.pricing.markup          ?? DEFAULT_PRICING.markup,
    taxRate:         p.pricing.taxRate         ?? DEFAULT_PRICING.taxRate,
    delivery:        p.pricing.delivery        ?? DEFAULT_PRICING.delivery,
  };
}

/** Build the customer-visible quote breakdown. Pure function. */
export function computeQuote(
  bom: ProjectBom,
  pricing: PricingConfig,
  cabinetCount: number,
): QuoteBreakdown {
  const materialsCost: USD = bom.totalMaterials;
  const edgesCost:     USD = bom.totalEdges;
  const hardwareCost:  USD = bom.totalHardware;
  const laborCost:     USD = cabinetCount * pricing.laborPerCabinet;

  const subtotal     = materialsCost + edgesCost + hardwareCost + laborCost;
  const markupAmount = subtotal * pricing.markup;
  const preTax       = subtotal + markupAmount;
  const taxAmount    = preTax * pricing.taxRate;
  const total        = preTax + taxAmount + pricing.delivery;

  return {
    materialsCost,
    edgesCost,
    hardwareCost,
    laborCost,
    subtotal,
    markupAmount,
    preTax,
    taxAmount,
    delivery: pricing.delivery,
    total,
    partCount: bom.partCount,
    cabinetCount,
  };
}

/** Default validity window for new quotes (30 days). */
export function defaultValidUntil(now = new Date()): string {
  const dt = new Date(now);
  dt.setDate(dt.getDate() + 30);
  return dt.toISOString();
}
